// Recent-ROM cache for the player harness.
//
// ROM *bytes* live in IndexedDB; the tiny recency *index* (an ordered
// list of {hash, name}, a few hundred bytes) stays in localStorage.
// Split rationale: localStorage shares a ~5MB origin quota with the
// Munos KV store (persistence-store.ts budgets 1MiB per namespace
// against that same quota), and base64-inflated ROM blobs (~683KB for
// a 512KB ROM, up to MAX_RECENT_ROMS of them) were starving script
// saves into `localstorage_full` while the KV's own tally said there
// was room. The index is too small to matter and a synchronous read
// at boot keeps the recent-list render simple, so it stays put.
//
// IndexedDB can be unavailable (e.g. some private-browsing modes) or
// refuse to open. Every entry point degrades to "no cache" — get()
// returns null, put() is a no-op — and never throws; the harness
// already handles missing bytes by asking the user to re-pick.

export interface RecentRom {
  hash: string;
  name: string;
}

const ROMS_INDEX_KEY = "roms:index";
// Pre-IndexedDB layout: ROM bytes were stored base64-encoded in
// localStorage under `rom:<hash>`. Only the one-time migration below
// still knows about this prefix.
const LEGACY_ROM_BYTES_PREFIX = "rom:";
export const MAX_RECENT_ROMS = 8;

const DB_NAME = "multinostalgia-rom-cache";
const DB_VERSION = 1;
const STORE_NAME = "roms";

// ----- IndexedDB plumbing -----

// Lazily-opened singleton connection. Resolves to null when IndexedDB
// is unavailable or the open fails — callers treat null as "no cache".
let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (!dbPromise) {
    dbPromise = new Promise<IDBDatabase | null>((resolve) => {
      try {
        if (typeof indexedDB === "undefined") {
          resolve(null);
          return;
        }
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
          if (!req.result.objectStoreNames.contains(STORE_NAME)) {
            req.result.createObjectStore(STORE_NAME);
          }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => {
          console.warn("[harness] ROM cache unavailable (IndexedDB open failed):", req.error);
          resolve(null);
        };
      } catch (e) {
        // indexedDB.open itself can throw synchronously (e.g. opaque
        // origins). Degrade to no cache.
        console.warn("[harness] ROM cache unavailable:", e);
        resolve(null);
      }
    });
  }
  return dbPromise;
}

// Run one request against the roms store. Resolves to undefined on any
// failure (no cache, transaction abort) — never rejects.
async function withStore<T>(
  mode: IDBTransactionMode,
  op: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | undefined> {
  const db = await openDb();
  if (!db) return undefined;
  try {
    return await new Promise<T | undefined>((resolve) => {
      const tx = db.transaction(STORE_NAME, mode);
      const req = op(tx.objectStore(STORE_NAME));
      tx.oncomplete = () => resolve(req.result);
      tx.onerror = () => resolve(undefined);
      tx.onabort = () => resolve(undefined);
    });
  } catch {
    return undefined;
  }
}

// ----- ROM bytes (IndexedDB) -----

export async function getRom(hash: string): Promise<Uint8Array | null> {
  const v: unknown = await withStore("readonly", store => store.get(hash));
  if (v instanceof Uint8Array) return v;
  // Defensive: structured clone round-trips Uint8Array as-is, but
  // tolerate a raw ArrayBuffer in case a future writer stores one.
  if (v instanceof ArrayBuffer) return new Uint8Array(v);
  return null;
}

// Best-effort: a failed write just means the ROM won't be in the
// recent list's cache next time. Returns true iff the bytes actually
// landed in IndexedDB — a successful put resolves to the stored key,
// while every degraded path (no IDB, transaction abort) resolves to
// undefined. The migration relies on this to avoid deleting a ROM's
// only copy before it's safely persisted.
export async function putRom(hash: string, bytes: Uint8Array): Promise<boolean> {
  const key = await withStore("readwrite", store => store.put(bytes, hash));
  return key !== undefined;
}

async function deleteRom(hash: string): Promise<void> {
  await withStore("readwrite", store => store.delete(hash));
}

// ----- recency index (localStorage) -----

export function readRecentRoms(): RecentRom[] {
  try {
    const raw = localStorage.getItem(ROMS_INDEX_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is RecentRom =>
        typeof e === "object" && e !== null &&
        typeof (e as RecentRom).hash === "string" &&
        typeof (e as RecentRom).name === "string",
    );
  } catch {
    return [];
  }
}

function writeIndex(index: RecentRom[]): void {
  try {
    localStorage.setItem(ROMS_INDEX_KEY, JSON.stringify(index));
  } catch (e) {
    console.warn("[harness] failed to persist ROM index:", e);
  }
}

// Bring `entry` to the front of the recent list and persist. Drops
// any older entry with the same hash (so loading the same ROM again
// just moves it to the top) and trims to MAX_RECENT_ROMS, evicting
// the cached bytes of anything that fell off the tail (fire-and-
// forget — eviction failure just leaves an orphaned blob in IDB).
export function rememberRom(entry: RecentRom): RecentRom[] {
  const prior = readRecentRoms().filter(e => e.hash !== entry.hash);
  const next = [entry, ...prior].slice(0, MAX_RECENT_ROMS);
  writeIndex(next);
  const kept = new Set(next.map(e => e.hash));
  for (const e of prior) {
    if (!kept.has(e.hash)) void deleteRom(e.hash);
  }
  return next;
}

// ----- one-time legacy migration -----

// Move any pre-IndexedDB base64 ROM blobs out of localStorage. A legacy
// blob's localStorage copy is dropped ONLY once its bytes are confirmed
// in IndexedDB — because getRom reads exclusively from IDB, so if the IDB
// write degraded to a no-op (IDB unavailable, e.g. a private-browsing
// session) an unconditional delete would destroy the ROM's only copy with
// no reader to benefit from the freed quota. Leaving the blob lets a later
// boot with IDB available migrate it successfully (private-mode outages are
// typically per-session). An empty/garbage legacy value carries nothing
// worth keeping, so it's removed regardless. Best-effort throughout — any
// failure is tolerated silently and retried on the next boot.
export async function migrateLegacyRomCache(): Promise<void> {
  try {
    const legacyKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k !== null && k.startsWith(LEGACY_ROM_BYTES_PREFIX)) legacyKeys.push(k);
    }
    for (const key of legacyKeys) {
      try {
        const b64 = localStorage.getItem(key);
        if (b64) {
          const persisted = await putRom(key.slice(LEGACY_ROM_BYTES_PREFIX.length), base64ToBytes(b64));
          if (persisted) localStorage.removeItem(key); // only after the bytes are safe in IDB
        } else {
          localStorage.removeItem(key); // empty/garbage value — nothing to preserve
        }
      } catch { /* tolerate per-key failures; remaining keys retry next boot */ }
    }
  } catch { /* localStorage itself unavailable — nothing to migrate */ }
}

function base64ToBytes(b64: string): Uint8Array {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}
